import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';

const NotificationsTab = ({ setMessage, setError }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await http.get('/admin/notifications', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setNotifications(response.data.notifications || []);
            }
        } catch (error) {
            console.error('Failed to load notifications:', error);
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const deleteNotification = async (notificationId) => {
        if (!window.confirm('Delete this notification permanently?')) return;

        try {
            const token = localStorage.getItem('token');
            await http.post(`/admin/notifications/${notificationId}/clear`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessage('Notification deleted');
            loadNotifications();
        } catch (error) {
            console.error('Failed to delete notification:', error);
            setError('Failed to delete notification');
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('token');
            await http.post(`/admin/notifications/${notificationId}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessage('Notification marked as read');
            loadNotifications();
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
            setError('Failed to mark notification as read');
        }
    };

    const deleteAllNotifications = async () => {
        if (!window.confirm(`Delete all ${notifications.length} notifications permanently? This cannot be undone.`)) return;

        try {
            const token = localStorage.getItem('token');
            await http.post('/admin/notifications/clear-all', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessage('All notifications deleted');
            loadNotifications();
        } catch (error) {
            console.error('Failed to delete all notifications:', error);
            setError('Failed to delete all notifications');
        }
    };

    useEffect(() => {
        loadNotifications();
    }, []);

    const unreadNotifications = notifications.filter(n => !n.read);
    const readNotifications = notifications.filter(n => n.read);

    // Helper function to get notification icon and color based on type
    const getNotificationStyle = (notif) => {
        const type = notif.type || notif.notification_type || 'default';

        switch (type) {
            case 'renewal_response':
            case 'subscription_renewal':
                return { iconType: 'refresh', badge: 'Renewal', color: 'from-blue-600 to-blue-500', borderColor: 'border-blue-400' };
            case 'update_request':
                return { iconType: 'document', badge: 'Update Request', color: 'from-purple-600 to-purple-500', borderColor: 'border-purple-400' };
            case 'profile_update':
            case 'customer_preference_update':
            case 'customer_feedback':
                return { iconType: 'document', badge: 'Profile Updated', color: 'from-teal-600 to-teal-500', borderColor: 'border-teal-400' };
            case 'subscription_unpause':
            case 'unpause_reminder':
                return { iconType: 'clock', badge: 'Unpaused', color: 'from-yellow-600 to-yellow-500', borderColor: 'border-yellow-400' };
            case 'subscription_pause':
            case 'pause_reminder':
                return { iconType: 'pause', badge: 'Paused', color: 'from-gray-600 to-gray-500', borderColor: 'border-gray-400' };
            case 'payment':
            case 'subscription_addon':
                return { iconType: 'cash', badge: 'Payment / Add-on', color: 'from-green-600 to-green-500', borderColor: 'border-green-400' };
            case 'subscription_cancelled':
                return { iconType: 'document', badge: 'Cancelled', color: 'from-red-600 to-red-500', borderColor: 'border-red-400' };
            default:
                return { iconType: 'bell', badge: 'Activity', color: 'from-orange-600 to-orange-500', borderColor: 'border-orange-400' };
        }
    };

    const getNotificationIcon = (iconType) => {
        switch (iconType) {
            case 'refresh':
                return <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>;
            case 'document':
                return <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
            case 'clock':
                return <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
            case 'pause':
                return <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
            case 'cash':
                return <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
            default:
                return <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>;
        }
    };

    return (
        <div className="space-y-4 bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg">
            {/* Header */}
            <div className="bg-gradient-to-r from-black to-gray-900 rounded-xl p-6 shadow-lg border-2 border-orange-500">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                            <svg className="h-7 w-7 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            Customer Activity Notifications
                            <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-lg font-bold">{notifications.length}</span>
                        </h3>
                        <p className="text-gray-300 text-sm mt-2">Real-time customer activity updates</p>
                    </div>
                    {notifications.length > 0 && (
                        <button
                            onClick={deleteAllNotifications}
                            className="bg-white text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-semibold shadow-lg transition-all hover:scale-105 flex items-center gap-2"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete All ({notifications.length})
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="bg-white p-8 rounded-xl shadow-lg text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-orange-500 mb-4"></div>
                    <p className="text-gray-700 text-base font-semibold">Loading notifications...</p>
                </div>
            ) : (
                <>
                    {/* Unread Notifications */}
                    {unreadNotifications.length > 0 && (
                        <div className="space-y-3">
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">Unread {unreadNotifications.length}</span>
                            </h3>
                            {unreadNotifications.map((notif) => {
                                const style = getNotificationStyle(notif);
                                return (
                                    <div key={notif.id} className={`bg-gradient-to-br ${style.color} rounded-xl p-4 shadow-lg border ${style.borderColor}`}>
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                    <span className="text-2xl">{getNotificationIcon(style.iconType)}</span>
                                                    <h4 className="font-bold text-white text-base">{notif.customer_name || 'System'}</h4>
                                                    <span className="bg-white text-gray-800 text-xs px-2 py-1 rounded-full font-bold">{style.badge}</span>
                                                    <span className="bg-white/30 text-white text-xs px-2 py-1 rounded-full font-bold">NEW</span>
                                                </div>
                                                <p className="text-white text-sm">{notif.message}</p>
                                                <p className="text-xs text-white/80 mt-2 flex items-center gap-1">
                                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {new Date(notif.created_at).toLocaleString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                        hour: 'numeric',
                                                        minute: '2-digit',
                                                        hour12: true
                                                    })}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <button
                                                    onClick={() => markAsRead(notif.id)}
                                                    className="bg-white text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg text-sm font-semibold ml-4 shadow transition-all hover:scale-105 whitespace-nowrap"
                                                >
                                                    Mark as Read
                                                </button>
                                                <button
                                                    onClick={() => deleteNotification(notif.id)}
                                                    className="bg-white text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg text-sm font-semibold ml-4 shadow transition-all hover:scale-105 whitespace-nowrap"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {/* Read Notifications */}
                    {readNotifications.length > 0 && (
                        <div className="space-y-3 mt-6">
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                <span className="bg-gray-600 text-white px-3 py-1 rounded-full text-sm">Read {readNotifications.length}</span>
                            </h3>
                            {readNotifications.map((notif) => {
                                const style = getNotificationStyle(notif);
                                return (
                                    <div key={notif.id} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border-2 border-gray-600 p-4 opacity-90 hover:opacity-100 hover:border-orange-500 transition-all shadow-lg">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                    <span className="text-xl">{getNotificationIcon(style.iconType)}</span>
                                                    <h4 className="font-bold text-white text-sm">{notif.customer_name || 'System'}</h4>
                                                    <span className="bg-gray-700 text-gray-200 text-xs px-2 py-1 rounded-full font-semibold">{style.badge}</span>
                                                </div>
                                                <p className="text-gray-300 text-sm mt-1">{notif.message}</p>
                                                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {new Date(notif.created_at).toLocaleString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                        hour: 'numeric',
                                                        minute: '2-digit',
                                                        hour12: true
                                                    })}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => deleteNotification(notif.id)}
                                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm font-semibold ml-4 shadow-lg transition-all hover:scale-105"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {/* Empty State */}
                    {notifications.length === 0 && (
                        <div className="bg-white p-8 rounded-xl shadow-lg text-center border-2 border-orange-500">
                            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13.41L18.59 12 20 10.59 18.59 9 17 10.59 15.41 9 14 10.59 15.59 12 14 13.41 15.41 15 17 13.41 18.59 15z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 8V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 12H8a2 2 0 01-2-2v-8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2z" />
                            </svg>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">No Notifications</h3>
                            <p className="text-gray-600 text-sm">
                                You'll see notifications here when customers make preference changes or submit requests.
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default NotificationsTab;
